"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrainerSim = void 0;
const trainer_1 = require("../engine/trainer");
const assert_1 = require("./assert");
const conversions_1 = require("./conversions");
const engines_1 = require("./engines");
const global_1 = require("./global");
const model_1 = require("./model");
const shot_1 = require("./shot");
const target_1 = require("./target");
class TrainerSim {
    static DefaultCourtPoint = { x: 0, y: 0, z: 0 };
    m_model;
    m_position;
    m_armAngle = 0;
    m_launchPoint = TrainerSim.DefaultCourtPoint;
    m_localizedCameraPosition;
    conversions;
    constructor(physicsModelName, sport) {
        (0, global_1.setPhysicsModel)(physicsModelName, sport);
        this.m_model = (0, model_1.createModel)(physicsModelName, sport, (0, model_1.copyTunables)((0, global_1.getPhysicsModel)()));
        this.conversions = new conversions_1.Conversions(this.m_model, this.engine);
        const { trainerGeometry } = this.m_model;
        this.m_position = { x: 0, y: 0, yaw: 0, h: trainerGeometry.liftOrigin.z };
        this.m_localizedCameraPosition = {
            x: 0,
            y: 0,
            z: 0,
            roll: 0,
            yaw: 0,
            pitch: 90 - trainerGeometry.cameraDownwardPitch,
        };
        this.SetPositionManual(this.m_position);
    }
    /** The engine for pose, camera and conversions. */
    get engine() {
        return (0, engines_1.baseEngine)(this.m_model.name, this.m_model.sport);
    }
    get pose() {
        const { x, y, yaw, h } = this.m_position;
        return { x, y, yaw, headHeight: h };
    }
    GetUserAidedLocalizationHeight(courtPoint, netClearanceFeet) {
        const { sport } = this.m_model;
        const { engine } = this;
        const upG = engine.referencePoint(courtPoint);
        (0, assert_1.assert)(upG !== undefined, `GetUserAidedLocalizationHeight: point ${courtPoint} is invalid for sport ${sport}`);
        const camera = this.m_localizedCameraPosition;
        const cpP = engine.fromLocalization(camera);
        (0, assert_1.assert)(engine.referencePoint("netpost_r_t") !== undefined, `GetUserAidedLocalizationHeight: net point netpost_r_t not found for sport ${sport}!`);
        const minFC = 0.1;
        const maxNC = -0.01;
        (0, assert_1.assert)(upG.y >= minFC, `GetUserAidedLocalizationHeight: court point ${courtPoint} y=${upG.y} must be > ${minFC} m (into far court)`);
        (0, assert_1.assert)(cpP.y <= maxNC, `GetUserAidedLocalizationHeight: cam pos y=${cpP.y} must be <= ${maxNC} (in near court)`);
        return engine.localizationHeadHeight(camera, courtPoint, netClearanceFeet);
    }
    SetPositionManual(position) {
        this.m_position = position;
        this.m_localizedCameraPosition = this.cameraAt(true);
        this.m_launchPoint = this.launchPointAt(0, 0);
    }
    SetPositionLocalized(localizedCameraPosition) {
        this.m_localizedCameraPosition = localizedCameraPosition;
        this.headAt(this.cameraHeadHeight(localizedCameraPosition.z));
        const pose = this.engine.poseFromCamera(localizedCameraPosition);
        this.m_position.x = pose.x;
        this.m_position.y = pose.y;
        this.m_position.yaw = pose.yaw;
        this.m_position.h = pose.headHeight;
        this.m_launchPoint = this.launchPointAt(0, 0);
    }
    GetPosition() {
        return this.m_position;
    }
    GetArmAngle() {
        return this.m_armAngle;
    }
    SetHeadHeight(height) {
        this.m_position.h = height;
        this.m_localizedCameraPosition = this.cameraAt(false);
        this.m_launchPoint = this.launchPointAt(0, 0);
    }
    GetLaunchPoint() {
        return this.m_launchPoint;
    }
    SetLaunchPoint(yawDeg, pitchDeg) {
        (0, assert_1.assert)(Math.abs(yawDeg) <= 90 && Math.abs(pitchDeg) <= 90, `SetLaunchPoint: yaw and pitch (${yawDeg}, ${pitchDeg}) must be in [-90, 90]`);
        this.m_launchPoint = this.launchPointAt(yawDeg, pitchDeg);
    }
    GetHeadHeight() {
        return this.m_position.h;
    }
    GetConfig() {
        return this.m_model;
    }
    /**
     * The `convert` functions bound to this sim's own model. Not in v1, whose
     * only conversions read the global model: use these to convert for this
     * sim when another sim may have set the global to a different sport.
     */
    GetConversions() {
        return this.conversions;
    }
    GetLocalizedCameraPosition() {
        return this.m_localizedCameraPosition;
    }
    Target(plannedShot) {
        const { result, launchPoint } = (0, target_1.target)({
            model: this.m_model,
            conversions: this.conversions,
            pose: this.pose,
            launchPoint: this.m_launchPoint,
            launchPointAt: (yaw, pitch) => this.launchPointAt(yaw, pitch),
        }, plannedShot);
        if (launchPoint !== null) {
            this.m_launchPoint = launchPoint;
        }
        return result;
    }
    Launch(launchParams) {
        (0, assert_1.assert)(launchParams.rpms !== null || launchParams.speedSpin !== null, "Launch: one of (speedSpin, rpms) must be set");
        const { simParams, constants } = this.m_model;
        (0, shot_1.assertFinish)(simParams, "Launch");
        const { kernel, profile } = (0, engines_1.enginesFor)(this.m_model);
        (0, engines_1.assertLaunchable)(profile);
        if (launchParams.speedSpin === null) {
            launchParams.speedSpin = this.conversions.rpm2speedspin(launchParams.rpms);
        }
        if (launchParams.rpms === null) {
            launchParams.rpms = this.conversions.speedspin2rpm(launchParams.speedSpin);
        }
        const { speedSpin } = launchParams;
        this.m_launchPoint = this.launchPointAt(launchParams.yaw, launchParams.pitch);
        if (speedSpin.spinLevel !== null && speedSpin.spinLevel > 0 && speedSpin.spin === null) {
            speedSpin.spin = this.conversions.spinlevel2ballspin(speedSpin.spinLevel);
        }
        // A null spin flies as v1 flies it: no lift, exactly as 0.
        const launch = {
            pitch: launchParams.pitch,
            yaw: launchParams.yaw,
            speed: speedSpin.speed,
            spin: { rpm: speedSpin.spin === null ? 0 : speedSpin.spin, axis: speedSpin.spinAxis },
        };
        const thrown = (0, trainer_1.release)(profile, this.pose, launch, "v1");
        const shot = new shot_1.ShotRecorder(this.m_launchPoint, simParams.duration, 0);
        kernel.record(thrown, shot, { until: (0, shot_1.untilFor)(simParams) });
        // v1's no-drag range, from _initializeLaunch
        const g = constants.gravity;
        const h = this.m_launchPoint.z;
        const vz = thrown.velocity.z;
        const vr = speedSpin.speed * Math.cos((thrown.pitch * Math.PI) / 180.0);
        return {
            ballPath: shot.ballPath,
            bounces: shot.bounces,
            range: vr * ((vz + Math.sqrt(vz * vz + 2.0 * g * h)) / g),
            peakHeight: shot.peakHeight,
            landingPoint: shot.landingPoint,
            launchPoint: { ...this.m_launchPoint },
            launchSpeed: speedSpin.speed,
            spinAxis: speedSpin.spinAxis,
            spin: speedSpin.spin,
            pitch: thrown.pitch,
            yaw: thrown.yaw,
            pitchDeflection: thrown.deflection.pitch,
            yawDeflection: thrown.deflection.yaw,
        };
    }
    Stroke(strikePoint, initialVelocity, spin = 0, spinAxis = 0) {
        const { simParams } = this.m_model;
        (0, shot_1.assertFinish)(simParams, "Stroke");
        const { x: vx, y: vy, z: vz } = initialVelocity;
        const shot = new shot_1.ShotRecorder(strikePoint, simParams.duration, strikePoint.z);
        (0, engines_1.enginesFor)(this.m_model).kernel.record((0, trainer_1.strokeRelease)(strikePoint, initialVelocity, { rpm: spin, axis: spinAxis }, "v1"), shot, {
            until: (0, shot_1.untilFor)(simParams),
            stopOutOfBounds: false,
        });
        return {
            ballPath: shot.ballPath,
            bounces: shot.bounces,
            peakHeight: shot.peakHeight,
            landingPoint: shot.landingPoint,
            range: -1,
            launchPoint: { ...strikePoint },
            launchSpeed: Math.sqrt(vx * vx + vy * vy + vz * vz),
            spinAxis,
            spin,
            pitch: (Math.atan2(vz, Math.sqrt(vx * vx + vy * vy)) * 180) / Math.PI,
            yaw: (Math.atan2(vx, vy) * 180) / Math.PI,
            pitchDeflection: 0,
            yawDeflection: 0,
        };
    }
    /**
     * The engine's reconstruction (free flight, rk4, v1's spin model), with
     * shot = the legacy Stroke of the fitted velocity. errorDistance is the
     * fit's free-flight miss; errorTime is 0, as v1's always is.
     */
    ReconstructStroke(strikePoint, landingPoint, flightTime, spin = 0, spinAxis = 0) {
        (0, assert_1.assert)(flightTime > 0.01, "ReconstructStroke: flightTime must be greater than 0.01 seconds");
        const reconstructed = (0, engines_1.enginesFor)(this.m_model).solver.reconstruct(strikePoint, landingPoint, flightTime, {
            rpm: spin,
            axis: spinAxis,
        });
        // v1 negates the spin of a stroke its no-drag seed sends toward -y, and so does the engine's fit.
        const g = this.m_model.constants.gravity;
        const seed = {
            x: (landingPoint.x - strikePoint.x) / flightTime,
            y: (landingPoint.y - strikePoint.y) / flightTime,
            z: (landingPoint.z - strikePoint.z) / flightTime + 0.5 * g * flightTime,
        };
        const adjustedSpin = seed.y < 0 ? -spin : spin;
        const fit = reconstructed.ok ? reconstructed : reconstructed.closest;
        const velocity = fit ? { ...fit.velocity } : seed;
        const { x, y, z } = velocity;
        return {
            initialVelocity: velocity,
            shot: this.Stroke(strikePoint, velocity, adjustedSpin, spinAxis),
            isConverged: reconstructed.ok,
            iterations: fit?.iterations ?? 0,
            errorDistance: fit?.miss ?? Infinity,
            errorTime: 0,
            launchSpeed: Math.sqrt(x * x + y * y + z * z),
            launchPitch: (Math.atan2(z, Math.sqrt(x * x + y * y)) * 180) / Math.PI,
            launchYaw: (Math.atan2(x, y) * 180) / Math.PI,
        };
    }
    // _______________________________________________________________________
    // Pose helpers: v1's assertions and arm-angle bookkeeping around the engine
    /** v1 _headPositionFromHeight: asserts the arm can reach, and moves it there. */
    headAt(headHeight) {
        const { liftArmLength: radius, liftOrigin } = this.m_model.trainerGeometry;
        const headZ = headHeight - liftOrigin.z;
        (0, assert_1.assert)(Math.abs(headZ) <= radius, `arm pos Z in lift coords ${headZ} must be in [-${radius}, ${radius}]`);
        this.m_armAngle = this.engine.armAngle(headHeight);
    }
    /** The head height v1 derives from a localized camera height, through millimeters. */
    cameraHeadHeight(cameraZ) {
        return cameraZ / 1000.0 - this.m_model.trainerGeometry.cameraOffsetFromHead.z;
    }
    /** v1 _computeLaunchPoint. */
    launchPointAt(yawDeg, pitchDeg) {
        this.headAt(this.m_position.h);
        (0, assert_1.assert)(Math.abs(yawDeg) <= 90 && Math.abs(pitchDeg) <= 90, `yaw and pitch (${yawDeg}, ${pitchDeg}) must be in [-90, 90]`);
        return this.engine.launchPoint(this.pose, pitchDeg, yawDeg);
    }
    /** v1 _computeLocalizedCameraPosition: the mounting's roll and pitch, or the current camera's. */
    cameraAt(changePitchRoll) {
        const { cameraOffsetFromHead } = this.m_model.trainerGeometry;
        this.headAt(this.cameraHeadHeight((this.m_position.h + cameraOffsetFromHead.z) * 1000.0));
        const { roll, pitch } = this.m_localizedCameraPosition;
        return this.engine.camera(this.pose, changePitchRoll ? undefined : { roll, pitch });
    }
}
exports.TrainerSim = TrainerSim;
